import React, { useState, useEffect } from 'react';
import config from '../config';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Alert,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { compressImageIfNeeded, getCompressionInfo, getCompressionOptions } from '../utils/imageCompression';

const OrderCreationAttachments = ({ 
  attachments = [], 
  onAttachmentsChange,
  maxFiles = 10,
  allowedTypes = ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv'],
  title = 'Attachments'
}) => {
  const [localAttachments, setLocalAttachments] = useState(attachments);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Update local attachments when prop changes
  useEffect(() => {
    setLocalAttachments(attachments);
  }, [attachments]);

  // Utility functions
  const isImageFile = (filename, mimeType) => {
    if (!filename && !mimeType) return false;
    
    // Check by MIME type first (more reliable)
    if (mimeType && mimeType.startsWith('image/')) return true;
    
    // Fallback to filename extension
    if (filename) {
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
    }
    
    return false;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes <= 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (mimeType && mimeType.startsWith('image/')) return <ImageIcon />;
    return <AttachFileIcon />;
  };

  // Handle file selection
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (localAttachments.length + files.length > maxFiles) {
      setMessage({
        type: 'error',
        text: `Maximum ${maxFiles} files allowed`
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage({ type: '', text: '' });

    try {
      const processedFiles = [];
      
      // Process each file (compress images if needed)
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(Math.round((i / files.length) * 100));
        
        let processedFile = file;
        
        if (isImageFile(file.name, file.type)) {
          try {
            const compressionOptions = getCompressionOptions('general');
            processedFile = await compressImageIfNeeded(file, compressionOptions);
            
            const compressionInfo = getCompressionInfo(file, processedFile);
            if (compressionInfo.wasCompressed) {
            }
          } catch (compressionError) {
            console.warn('Image compression failed, using original:', compressionError);
            processedFile = file;
          }
        }

        // Create temporary attachment object
        const tempAttachment = {
          id: `temp_${Date.now()}_${i}`,
          file: processedFile,
          originalName: file.name,
          fileSize: processedFile.size,
          mimeType: processedFile.type,
          preview: null
        };

        // Create preview for images
        if (isImageFile(file.name, file.type)) {
          tempAttachment.preview = URL.createObjectURL(processedFile);
        }

        processedFiles.push(tempAttachment);
      }

      const updatedAttachments = [...localAttachments, ...processedFiles];
      setLocalAttachments(updatedAttachments);
      
      if (onAttachmentsChange) {
        onAttachmentsChange(updatedAttachments);
      }

      setMessage({
        type: 'success',
        text: `${files.length} file(s) added successfully`
      });
    } catch (error) {
      console.error('Error processing files:', error);
      setMessage({
        type: 'error',
        text: 'Error processing files'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Clear file input
      event.target.value = '';
    }
  };

  // Handle attachment removal
  const handleRemoveAttachment = (attachmentId) => {
    const attachmentToRemove = localAttachments.find(att => att.id === attachmentId);
    
    // Clean up preview URL if it exists
    if (attachmentToRemove && attachmentToRemove.preview) {
      URL.revokeObjectURL(attachmentToRemove.preview);
    }

    const updatedAttachments = localAttachments.filter(att => att.id !== attachmentId);
    setLocalAttachments(updatedAttachments);
    
    if (onAttachmentsChange) {
      onAttachmentsChange(updatedAttachments);
    }

    setMessage({
      type: 'success',
      text: 'Attachment removed'
    });
  };

  // Handle image viewing
  const handleViewImage = (attachment) => {
    if (attachment.preview) {
      window.open(attachment.preview, '_blank');
    } else if (attachment.fromOffer && attachment.id) {
      // For attachments from offers, download via API
      window.open(`${config.apiUrl}/api/offer-attachments/download/${attachment.id}`, '_blank');
    }
  };

  // Clear messages after delay
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      localAttachments.forEach(attachment => {
        if (attachment.preview) {
          URL.revokeObjectURL(attachment.preview);
        }
      });
    };
  }, []);

  return (
    <Box>
      {/* Upload Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            disabled={uploading || localAttachments.length >= maxFiles}
            fullWidth
          >
            {uploading ? 'Processing...' : 'Add Files'}
            <input
              type="file"
              hidden
              multiple
              accept={allowedTypes.join(',')}
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </Button>
          
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                Processing files... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Maximum {maxFiles} files. Supported types: Images, PDF, Word, Excel, Text files.
          <br />
          Maximum file size: 50MB per file. Images will be automatically compressed if too large.
          <br />
          Files will be uploaded after the order is created.
        </Typography>
      </Paper>

      {/* Message Display */}
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {/* Attachments Display */}
      <Box>
        {localAttachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No attachments added
          </Typography>
        ) : (
          <Stack spacing={2}>
            {localAttachments.map((attachment) => (
              <Card key={attachment.id} sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Image Preview or File Icon */}
                <Box sx={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                  {attachment.preview ? (
                    <CardMedia
                      component="img"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      image={attachment.preview}
                      alt={attachment.originalName}
                      onClick={() => handleViewImage(attachment)}
                      onError={(e) => {
                        // Fall back to a generic file icon if the preview URL fails to
                        // load (e.g. transient network issue), instead of a broken image icon.
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <Box 
                    sx={{ 
                      display: attachment.preview ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      flexDirection: 'column'
                    }}
                  >
                    {getFileIcon(attachment.mimeType)}
                  </Box>
                </Box>

                {/* File Details */}
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" noWrap>
                    {attachment.originalName || attachment.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(attachment.fileSize || attachment.size)}
                  </Typography>
                  {attachment.fromOffer ? (
                    <Chip 
                      label="From Offer" 
                      size="small" 
                      color="info" 
                      sx={{ ml: 1 }} 
                    />
                  ) : (
                    <Chip 
                      label="Pending upload" 
                      size="small" 
                      color="warning" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </CardContent>

                {/* Actions */}
                <Box sx={{ p: 1 }}>
                  {(attachment.preview || (attachment.fromOffer && isImageFile(attachment.name || attachment.originalName, attachment.type || attachment.mimeType))) && (
                    <Tooltip title="View">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewImage(attachment)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Tooltip title="Remove">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleRemoveAttachment(attachment.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default OrderCreationAttachments;